import React from 'react'
import {ClientMetricCard} from './ClientMetricCard'
import {StatusBarChart} from './StatusBarChart'
import type {ScheduledServicesMetrics, StatusBucket} from './_requests'

interface Props {
  metrics: ScheduledServicesMetrics
  statusBuckets: StatusBucket[]
  periodLabel: string
}

const CalendarIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='3' y='4' width='18' height='18' rx='2' ry='2' />
    <line x1='16' y1='2' x2='16' y2='6' />
    <line x1='8' y1='2' x2='8' y2='6' />
    <line x1='3' y1='10' x2='21' y2='10' />
  </svg>
)

const ClockIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <polyline points='12 6 12 12 16 14' />
  </svg>
)

const TrendIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='23 6 13.5 15.5 8.5 10.5 1 18' />
    <polyline points='17 6 23 6 23 12' />
  </svg>
)

export const ScheduledServicesSection: React.FC<Props> = ({metrics, statusBuckets, periodLabel}) => {
  return (
    <section className='cp-section'>
      <div className='cp-section__header'>
        <div className='cp-section__icon'>
          <CalendarIcon />
        </div>
        <div>
          <h2 className='cp-section__title'>Scheduled Services (IPI + VIS) - {periodLabel}</h2>
          <p className='cp-section__subtitle'>
            In-Person and Video Interpretation scheduling performance.
          </p>
        </div>
      </div>

      <div className='cp-metrics-row'>
        <ClientMetricCard
          title='Total Appointments'
          value={metrics.totalAppointments.toLocaleString()}
          subtext={`${metrics.totalMinutes.toLocaleString()} minutes total`}
          icon={<CalendarIcon />}
          tooltip={`Total scheduled IPI and VIS appointments in the selected period (${periodLabel}), with cumulative minutes shown underneath.`}
        />
        <ClientMetricCard
          title='Fulfillment'
          value={`${metrics.fulfillmentRate}%`}
          icon={<ClockIcon />}
          highlight
          tooltip='Appointments completed divided by total appointments scheduled for IPI and VIS, multiplied by 100.'
        />
        <ClientMetricCard
          title='Cancellation'
          value={`${metrics.cancellationRate}%`}
          subtext='Average'
          icon={<TrendIcon />}
          tooltip={`Cancelled scheduled appointments divided by total scheduled IPI and VIS appointments over the selected period (${periodLabel}), multiplied by 100.`}
        />
        <ClientMetricCard
          title='AVG Duration'
          value={metrics.avgDuration || '0m'}
          icon={<ClockIcon />}
          tooltip={`Average length of a scheduled IPI or VIS session in the selected period (${periodLabel}).`}
        />
      </div>

      <StatusBarChart
        title='Assignments By Status'
        data={statusBuckets}
        completedLabel='Completed'
        cancelledLabel='Cancelled'
      />
    </section>
  )
}
